import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertTriangle, XCircle, Info, Inbox } from 'lucide-react';

// The shared building blocks of every GTPL screen (styles: styles/gtpl.css).
// tone: 'ok' | 'warn' | 'bad' | 'info' | 'adv' | 'mute'  (see the colour meanings at the top of gtpl.css)

const cx = (...a) => a.filter(Boolean).join(' ');
export const TONE_ICON = { ok: CheckCircle, warn: AlertTriangle, bad: XCircle, info: Info, adv: Info, mute: Info };

// Wraps a screen. `narrow` for single-column forms.
export function Screen({ narrow, children }) {
  return <div className={cx('gtpl-ui', narrow && 'is-narrow')}>{children}</div>;
}

export function PageHead({ icon: Icon, tone = 'info', title, sub, children }) {
  return (
    <header className={cx('gx-head', `is-${tone}`)}>
      {Icon && <span className="gx-head-icon"><Icon size={22} /></span>}
      <div className="gx-head-text"><h1>{title}</h1>{sub && <p>{sub}</p>}</div>
      {children && <div className="gx-head-actions">{children}</div>}
    </header>
  );
}

// A card. `collapsible` makes it an accordion (<details>), `open` is its starting state.
export function Panel({ icon: Icon, tone = 'info', title, sub, count, right, collapsible, open = true, flush, tinted, children, style }) {
  const head = (
    <>
      {Icon && <span className="gx-panel-ico"><Icon size={16} /></span>}
      <div className="gx-panel-title">{title}{sub && <small>{sub}</small>}</div>
      {count != null && <span className="gx-count">{count}</span>}
      {right}
    </>
  );
  const klass = cx('gx-panel', `is-${tone}`, flush && 'is-flush', tinted && 'is-tinted');
  if (collapsible) {
    return (
      <details className={klass} open={open} style={style}>
        <summary>{head}</summary>
        <div className="gx-panel-body">{children}</div>
      </details>
    );
  }
  return (
    <section className={klass} style={style}>
      {title != null && <div className="gx-panel-head">{head}</div>}
      <div className="gx-panel-body">{children}</div>
    </section>
  );
}

export function Pill({ tone = 'mute', dot, children, title }) {
  return <span className={cx('gx-pill', `is-${tone}`)} title={title}>{dot && <i className="gx-dot" />}{children}</span>;
}

export function Count({ tone = 'info', children }) {
  return <span className={cx('gx-count', `is-${tone}`)}>{children}</span>;
}

// Banner. Pass `to` to make the whole banner a link; `actions` for buttons under the text.
export function Notice({ tone = 'info', icon, title, children, actions, to }) {
  const Icon = icon || TONE_ICON[tone];
  const body = (
    <>
      <Icon size={18} />
      <div className="gx-notice-body">
        {title && <b>{title}</b>}{title && children ? ' ' : ''}{children}
        {actions && <div className="gx-notice-actions">{actions}</div>}
      </div>
    </>
  );
  return to
    ? <Link to={to} className={cx('gx-notice', `is-${tone}`)}>{body}</Link>
    : <div className={cx('gx-notice', `is-${tone}`)} role={tone === 'bad' ? 'alert' : undefined}>{body}</div>;
}

export function Stats({ children }) { return <div className="gx-stats">{children}</div>; }

// A number tile. With onClick it is a filter button (`active` highlights it).
export function Stat({ tone, value, label, onClick, active, to }) {
  const inner = <><span className="gx-stat-n">{value}</span><span className="gx-stat-l">{label}</span></>;
  const klass = cx('gx-stat', tone && `is-${tone}`, active && 'is-active');
  if (to) return <Link to={to} className={klass}>{inner}</Link>;
  if (onClick) return <button type="button" className={klass} onClick={onClick} aria-pressed={!!active}>{inner}</button>;
  return <div className={klass}>{inner}</div>;
}

// Segmented tabs. tabs: [{ key, label, count?, icon? }]
export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="gx-tabs" role="tablist">
      {tabs.map(t => (
        <button key={t.key} type="button" role="tab" aria-selected={value === t.key}
          className={cx('gx-tab', value === t.key && 'is-on')} onClick={() => onChange(t.key)}>
          {t.icon && <t.icon size={15} />}{t.label}{t.count != null && <span className={cx('gx-count', `is-${t.tone || 'mute'}`)}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

// A labelled form field. children = the control.
export function Field({ label, hint, children, style }) {
  return <div className="gx-field" style={style}><label>{label}</label>{children}{hint && <div className="gx-hint">{hint}</div>}</div>;
}

// Read-only details. items: [[label, value, wide?]]; empty values are skipped.
export function Details({ items }) {
  return (
    <dl className="gx-kv">
      {items.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v, wide]) => (
        <div key={k} className={wide ? 'is-wide' : undefined}><dt>{k}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  );
}

// One mobile-friendly row card. Use inside <div className="gx-list">.
export function Item({ tone, title, sub, right, meta, actions, selected, left }) {
  return (
    <div className={cx('gx-item', tone && `is-${tone}`, selected && 'is-selected')}>
      <div className="gx-item-top">
        {left}
        <div className="gx-item-main"><div className="gx-item-title">{title}</div>{sub && <div className="gx-item-sub">{sub}</div>}</div>
        {right}
      </div>
      {meta && <div className="gx-item-meta">{meta}</div>}
      {actions && <div className="gx-item-actions">{actions}</div>}
    </div>
  );
}

export function Empty({ icon: Icon = Inbox, tone, title, children }) {
  return <div className={cx('gx-empty', tone && `is-${tone}`)}><Icon size={30} /><b>{title}</b>{children && <span>{children}</span>}</div>;
}

// On/off switch. Green when on (something is running), grey when off.
export function Switch({ checked, onChange, disabled, label }) {
  return (
    <button type="button" role="switch" aria-checked={!!checked} aria-label={label} disabled={disabled}
      className={cx('gx-switch', checked && 'is-on')} onClick={() => onChange(!checked)}><i /></button>
  );
}

// A setting: bold name, plain-words explanation, switch on the right.
export function SwitchRow({ title, children, checked, onChange, disabled, onText = 'On', offText = 'Off' }) {
  return (
    <div className="gx-switchrow">
      <div className="gx-switchrow-text">
        <b>{title}</b>
        <span>{children}</span>
      </div>
      <div className="gx-switchrow-ctl">
        <Pill tone={checked ? 'ok' : 'mute'} dot>{checked ? onText : offText}</Pill>
        <Switch checked={checked} onChange={onChange} disabled={disabled} label={title} />
      </div>
    </div>
  );
}

export function Pager({ page, pages, total, onPage }) {
  if (!pages || pages <= 1) return null;
  return (
    <div className="gx-actions" style={{ justifyContent: 'center', marginTop: 14 }}>
      <button type="button" className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span style={{ color: 'var(--text-muted)', fontSize: '.84rem' }}>Page {page} of {pages}{total != null ? ` (${total} total)` : ''}</span>
      <button type="button" className="btn btn-sm btn-secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
}

export function Loading({ children = 'Loading…' }) {
  return <div className="gx-empty"><span><i className="spinner" style={{ display: 'inline-block', width: 18, height: 18, borderWidth: 2, verticalAlign: -4, marginRight: 8 }} />{children}</span></div>;
}

export function Progress({ value, max, tone = 'info' }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return <div className={cx('gx-bar', `is-${tone}`)} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${pct}%` }} /></div>;
}

// Status word → tone, shared so "failed" is red and "queued" is amber on every screen.
const STATUS_TONE = {
  done: 'ok', success: 'ok', completed: 'ok', ok: 'ok', active: 'ok', matched: 'ok', connected: 'ok',
  queued: 'warn', pending: 'warn', waiting: 'warn', running: 'info', in_progress: 'info', scheduled: 'info',
  failed: 'bad', error: 'bad', expired: 'bad', inactive: 'bad', suspended: 'warn', cancelled: 'mute', canceled: 'mute',
};
export const toneOfStatus = s => STATUS_TONE[String(s || '').toLowerCase().replace(/\s+/g, '_')] || 'mute';
export function StatusPill({ status, children }) {
  return <Pill tone={toneOfStatus(status)} dot>{children || String(status || '—')}</Pill>;
}
